import { memo, type PointerEvent as ReactPointerEvent, type TouchEvent as ReactTouchEvent, type MouseEvent as ReactMouseEvent } from 'react'
import type { ChecklistItem } from '../../types/schema'
import type { TimelineElement } from '../../types/timelineElement'
import type { ResourceSignal } from '../../types/resource'
import { ResourceBadges } from '../Resources/ResourceBadges'
import { RecurringOccurrenceChip } from './RecurringOccurrenceChip'
import { StatusChip, DependencyLockChip } from '../Common'
import { Pressable } from '../Common/Pressable'
import { useRecurringLens } from '../../context/recurringLensContextCore'
import { useNls } from '../../nls/nlsProvider'

type ExternalLock = {
  lockedByName: string
}

type BoardNodeProps = {
  node: ChecklistItem
  x: number
  y: number
  width: number
  minHeight: number
  backgroundColor: string
  isLocked: boolean
  isExpired: boolean
  externalLock: ExternalLock | null
  externalBlocker: TimelineElement | null
  assigneeName: string
  assigneeRole: string
  hasAssignee: boolean
  resourceSignals: ResourceSignal[]
  parentPath: string
  parentColor: string
  onNodePointerDown: (event: ReactPointerEvent<HTMLElement>, node: ChecklistItem) => void
  onAnchorPointerDown: (event: ReactPointerEvent<HTMLElement>, node: ChecklistItem, anchor: 'top' | 'bottom') => void
  onMouseEnterNode: (event: ReactMouseEvent<HTMLElement>, node: ChecklistItem, parentPath: string, parentColor: string) => void
  onMouseMoveNode: (event: ReactMouseEvent<HTMLElement>, node: ChecklistItem) => void
  onMouseLeaveNode: () => void
  onTouchStartNode: (event: ReactTouchEvent<HTMLElement>, node: ChecklistItem, parentPath: string, parentColor: string) => void
  onTouchEndNode: () => void
}

const signalSignature = (signals: ResourceSignal[]) =>
  signals.map((signal) => `${signal.kind}:${signal.severity}:${signal.label}:${signal.summary}`).join('|')

const BoardNodeComponent = ({
  node,
  x,
  y,
  width,
  minHeight,
  backgroundColor,
  isLocked,
  isExpired,
  externalLock,
  externalBlocker,
  assigneeName,
  assigneeRole,
  hasAssignee,
  resourceSignals,
  parentPath,
  parentColor,
  onNodePointerDown,
  onAnchorPointerDown,
  onMouseEnterNode,
  onMouseMoveNode,
  onMouseLeaveNode,
  onTouchStartNode,
  onTouchEndNode,
}: BoardNodeProps) => {
  const { t } = useNls()
  // When mounted inside a RecurringLensProvider (Daily / Weekly canvas), the
  // tick state we render must reflect `occurrences[focusedKey]`, not the
  // top-level `isCompleted` mirror. Reads on Main canvas pass through
  // unchanged (lens === null).
  const lens = useRecurringLens()
  const isRecurring = node.checklistType === 'DAILY' || node.checklistType === 'WEEKLY'
  const occurrence = lens && isRecurring ? node.occurrences?.[lens.focusedKey] : null
  const effectiveWorkflowStatus = occurrence?.workflowStatus ?? node.workflowStatus
  const effectiveIsCompleted = occurrence ? Boolean(occurrence.isCompleted) : Boolean(node.isCompleted)

  // Derived time-status is projected onto every ChecklistItem by
  // checklistViewAdapter — carries either the per-occurrence status
  // (recurring under lens) or the definition-level status (individual).
  // Surfaces here as a data attribute so CSS can mute archived and hint
  // upcoming without touching every render path.
  const timeStatus = node.timeStatus ?? 'active'
  return (
  <article
    data-board-bubble-id={node.id}
    data-orbit-target="checklist-node"
    data-checklist-id={node.id}
    data-orbit-title={node.title}
    data-time-status={timeStatus}
    className={`checklistBubble ${effectiveIsCompleted ? 'isDone' : ''} ${isLocked ? 'isLocked' : ''} ${node.isPendingReview ? 'isPendingReview' : ''} ${node.isUrgentCrisisMode ? 'isCrisisMode' : ''} ${isExpired ? 'isTimerExpired' : ''} ${externalLock ? 'collaborationLockedSurface' : ''} ${hasAssignee ? 'isAssigned' : 'isWorkDisabled'} time-status-${timeStatus}`}
    style={{
      left: x,
      top: y,
      width,
      minHeight,
      backgroundColor,
    }}
    onMouseEnter={(event) => onMouseEnterNode(event, node, parentPath, parentColor)}
    onMouseMove={(event) => onMouseMoveNode(event, node)}
    onMouseLeave={onMouseLeaveNode}
    onTouchStart={(event) => onTouchStartNode(event, node, parentPath, parentColor)}
    onTouchEnd={onTouchEndNode}
  >
    <Pressable
      aria-label={t('NLSChecklistAriaIncomingWireAnchor')}
      className="checklistCap checklistCap--top"
      data-board-anchor-item-id={node.id}
      data-role="incoming"
      onPointerDown={(event) => onAnchorPointerDown(event, node, 'top')}
    />
    <Pressable
      aria-label={t('NLSChecklistAriaOutgoingWireAnchor')}
      className="checklistCap checklistCap--bottom"
      data-board-anchor-item-id={node.id}
      data-role="outgoing"
      onPointerDown={(event) => onAnchorPointerDown(event, node, 'bottom')}
    />
    {isLocked && externalBlocker && (
      <div className="checklistLockOverlay">
        {t('NLSChecklistBoardNodeLockedByBlocker', { title: externalBlocker.title })}
      </div>
    )}
    {effectiveWorkflowStatus === 'NOT_REQUIRED' && (
      <StatusChip size="sm" tone="muted" className="checklistNotNeededBadge">
        {t('NLSChecklistStatusNotNeeded')}
      </StatusChip>
    )}
    {isLocked && !externalBlocker && (
      <DependencyLockChip
        isLocked={isLocked}
        label={t('NLSChecklistStatusBlocked')}
        className="checklistLockBadge"
      />
    )}
    {isRecurring && node.recurringWindow?.endDate && lens && lens.focusedKey > node.recurringWindow.endDate.slice(0, 10) && (
      <StatusChip size="sm" tone="muted" className="checklistWindowEndedBadge">
        {t('NLSChecklistWindowEnded')}
      </StatusChip>
    )}
    {node.isPendingReview && (
      <StatusChip size="sm" tone="warning" className="checklistPendingReviewBadge">
        {t('NLSChecklistBoardNodePendingCosign')}
      </StatusChip>
    )}
    {externalLock && (
      <StatusChip size="sm" tone="danger" className="collaborationLockBadge">
        {t('NLSChecklistBoardNodeLockedBy', { name: externalLock.lockedByName })}
      </StatusChip>
    )}
    {hasAssignee ? (
      <div className="checklistAssigneeBadge" title={t('NLSChecklistBoardNodeAssignedTo', { name: assigneeName })}>
        <span>{assigneeName}</span>
        <small>{assigneeRole}</small>
      </div>
    ) : (
      <StatusChip size="sm" tone="muted" className="checklistWorkDisabledBadge">
        {t('NLSChecklistBoardNodeAssignBeforeWork')}
      </StatusChip>
    )}
    <div
      className="checklistBubbleContent"
      onPointerDown={(event) => onNodePointerDown(event, node)}
    >
      <span className="checklistBubbleTitleReadonly" title={node.title}>{node.title}</span>
      {isRecurring && (
        <div
          className="checklistBubbleRecurringChip"
          // Stop pointerdown from bubbling into the bubble's drag handler —
          // clicks on the chip shouldn't drag the whole card.
          onPointerDown={(event) => event.stopPropagation()}
        >
          <RecurringOccurrenceChip
            item={node}
            focusedKey={lens?.focusedKey}
            onJump={lens?.onFocusedDateChange}
          />
        </div>
      )}
      <ResourceBadges signals={resourceSignals} compact />
    </div>
  </article>
  )
}

export const BoardNode = memo(BoardNodeComponent, (prev, next) =>
  prev.node === next.node
  && prev.x === next.x
  && prev.y === next.y
  && prev.width === next.width
  && prev.minHeight === next.minHeight
  && prev.backgroundColor === next.backgroundColor
  && prev.isLocked === next.isLocked
  && prev.isExpired === next.isExpired
  && prev.externalLock?.lockedByName === next.externalLock?.lockedByName
  && prev.externalBlocker?.id === next.externalBlocker?.id
  && prev.externalBlocker?.title === next.externalBlocker?.title
  && prev.assigneeName === next.assigneeName
  && prev.assigneeRole === next.assigneeRole
  && prev.hasAssignee === next.hasAssignee
  && prev.parentPath === next.parentPath
  && prev.parentColor === next.parentColor
  && signalSignature(prev.resourceSignals) === signalSignature(next.resourceSignals)
  && prev.onNodePointerDown === next.onNodePointerDown
  && prev.onAnchorPointerDown === next.onAnchorPointerDown
  && prev.onMouseEnterNode === next.onMouseEnterNode
  && prev.onMouseMoveNode === next.onMouseMoveNode
  && prev.onMouseLeaveNode === next.onMouseLeaveNode
  && prev.onTouchStartNode === next.onTouchStartNode
  && prev.onTouchEndNode === next.onTouchEndNode
)
